import { Fragment } from "react";
import { Row, Table as TableComponent } from "@tanstack/react-table";
import { chunkBy } from "./utils";
import classNames from "classnames";
import { renderColumnValue } from "./utils";
import { SharedProps } from "./Table";
import { Link } from "react-router-dom";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { TABLE_ROW_HOVER_CLASSES } from "@/lib/styleHelpers";

export interface TableBodyProps<T> extends SharedProps<T> {
  table: TableComponent<T>;
  onClickBodyRow: (row: Row<T>) => (event: React.MouseEvent) => void;
  getRowClasses?: (row: Row<T>) => string;
  activeRowId?: string | null;
}

export const TableBody = <T,>({
  table,
  onClickBodyRow,
  renderExpandedRow,
  onClickRow,
  getRowLink,
  getRowClasses,
  activeRowId
}: TableBodyProps<T>) => {
  const { isCompactLayout } = useIsCompactLayout();
  const renderExpanded = (row: Row<T>) => !!renderExpandedRow && row.getIsExpanded();

  return chunkBy(table.getRowModel().rows, (row) => (renderExpanded(row) ? row.id : false)).map((chunk) => (
    <tbody role="rowgroup" key={chunk[0]?.id}>
      {chunk.map((row) => {
        const rowLink = getRowLink?.(row);
        const isClickable = !!onClickRow || !!rowLink;
        const customRowClasses = getRowClasses?.(row) || "";
        const hasCustomRowClasses = !!customRowClasses && customRowClasses !== "group-last:h-[60px]";
        const isKeyboardActive = activeRowId === row.id;
        const keyboardActiveClass = isKeyboardActive ? "keyboard-active-row" : "";

        const tableRow = (
          <tr
            className={classNames("border-t group bg-sys-brand-surface", customRowClasses, keyboardActiveClass, {
              [`cursor-pointer ${TABLE_ROW_HOVER_CLASSES}`]: isClickable && !hasCustomRowClasses
            })}
            onClick={!rowLink ? onClickBodyRow(row) : undefined}
            data-testid="body-row"
            data-table-row-id={row.id}
            data-keyboard-active={isKeyboardActive ? "true" : undefined}
            aria-current={isKeyboardActive ? "true" : undefined}
          >
            {row.getVisibleCells().map((cell, index) => (
              <td
                key={cell.id}
                className={classNames(
                  "text-body-medium py-2 h-12 align-middle",
                  !cell.column.columnDef.meta?.className?.includes("text-right") &&
                    !cell.column.columnDef.meta?.className?.includes("text-center") &&
                    "text-left",
                  {
                    "sticky left-0 z-10": index === 0,
                    "sticky right-0 z-10": cell.column.id === "actions",
                    "!bg-sys-brand-surface": (index === 0 || cell.column.id === "actions") && !hasCustomRowClasses
                  },
                  (index === 0 || cell.column.id === "actions") && hasCustomRowClasses && customRowClasses,
                  (index === 0 || cell.column.id === "actions") && keyboardActiveClass,
                  (row.original as any)?.indent === true ? "lg:pl-10 px-4" : "px-4",
                  cell.column.columnDef.meta?.className
                )}
                style={{
                  minWidth: cell.column.columnDef.minSize
                }}
              >
                {renderColumnValue(cell, isCompactLayout)}
              </td>
            ))}
          </tr>
        );

        return (
          <Fragment key={row.id}>
            {rowLink ? (
              <Link {...rowLink} style={{ display: "contents" }}>
                {tableRow}
              </Link>
            ) : (
              tableRow
            )}
            {renderExpanded(row) && (
              <tr data-testid="body-row">
                <td colSpan={row.getVisibleCells().length}>{renderExpandedRow?.({ row })}</td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </tbody>
  ));
};
